import React, { Component, createRef } from "react";

type ModalKind = "reactance" | "fortyTwo" | null;

interface State {
  openModal: ModalKind;
  firing: boolean;
}

class EasterEggs extends Component<{}, State> {
  canvasRef = createRef<HTMLCanvasElement>();
  lastFocus: HTMLElement | null = null;
  logoClickCount = 0;
  logoResetTimer: number | null = null;

  state: State = {
    openModal: null,
    firing: false,
  };

  componentDidMount() {
    (window as any).fireConfetti = this.fireConfetti;
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("click", this.handleLogoClick);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("click", this.handleLogoClick);
    document.body.style.overflow = "";
    if ((window as any).fireConfetti === this.fireConfetti) {
      delete (window as any).fireConfetti;
    }
    if (this.logoResetTimer) window.clearTimeout(this.logoResetTimer);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.state.openModal) {
      this.closeModal();
    }
  };

  handleLogoClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const logo = target.closest(".js-forty-two-logo");

    if (!logo) return;

    this.logoClickCount += 1;
    if (this.logoResetTimer) window.clearTimeout(this.logoResetTimer);
    this.logoResetTimer = window.setTimeout(() => {
      this.logoClickCount = 0;
    }, 8000);

    if (this.logoClickCount === 42) {
      event.preventDefault();
      this.logoClickCount = 0;
      this.fireConfetti();
      window.setTimeout(() => this.openModal("fortyTwo"), 250);
    }
  };

  openModal = (modal: ModalKind) => {
    this.lastFocus = document.activeElement as HTMLElement;
    document.body.style.overflow = "hidden";
    this.setState({ openModal: modal }, () => {
      window.setTimeout(() => {
        const closeButton = document.querySelector(
          ".egg-modal[aria-hidden='false'] .egg-modal-x",
        ) as HTMLElement;
        if (closeButton) closeButton.focus();
      }, 100);
    });
  };

  closeModal = () => {
    this.setState({ openModal: null });
    document.body.style.overflow = "";
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  fireConfetti = () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const ratio = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * ratio;
    canvas.height = window.innerHeight * ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const colours = [
      "#527FBE",
      "#FF645E",
      "#F5B041",
      "#16a34a",
      "#2D1B36",
      "#F3EC7A",
    ];
    const pieces = Array.from({ length: 130 }).map(() => ({
      x: window.innerWidth / 2,
      y: window.innerHeight * 0.28,
      vx: (Math.random() - 0.5) * 11,
      vy: Math.random() * -9 - 3,
      size: Math.random() * 7 + 4,
      rotate: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.25,
      colour: colours[Math.floor(Math.random() * colours.length)],
      life: 90 + Math.random() * 25,
    }));

    this.setState({ firing: true });

    const tick = () => {
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);

      pieces.forEach((piece) => {
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.vy += 0.28;
        piece.rotate += piece.vr;
        piece.life -= 1;

        if (piece.life <= 0) return;

        context.save();
        context.translate(piece.x, piece.y);
        context.rotate(piece.rotate);
        context.fillStyle = piece.colour;
        context.fillRect(
          -piece.size / 2,
          -piece.size / 2,
          piece.size,
          piece.size * 0.6,
        );
        context.restore();
      });

      if (pieces.some((piece) => piece.life > 0)) {
        requestAnimationFrame(tick);
      } else {
        context.clearRect(0, 0, window.innerWidth, window.innerHeight);
        this.setState({ firing: false });
      }
    };

    requestAnimationFrame(tick);
  };

  renderModal = (
    kind: Exclude<ModalKind, null>,
    children: React.ReactNode,
    titleId: string,
  ) => (
    <div
      className="egg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-hidden={this.state.openModal === kind ? "false" : "true"}
    >
      <button
        type="button"
        className="egg-modal-backdrop"
        aria-label="Close"
        onClick={this.closeModal}
      />
      <div className="egg-modal-card">{children}</div>
    </div>
  );

  render() {
    return (
      <>
        {/* Named by its own words. A separate label made screen readers say
            something other than what is on the button, and voice control
            couldn't press it by saying "click Don't click this". */}
        <button
          type="button"
          className="dont-click-btn"
          onClick={() => this.openModal("reactance")}
        >
          Don't click this
        </button>

        {this.renderModal(
          "reactance",
          <>
            <button
              type="button"
              className="egg-modal-x"
              aria-label="Close"
              onClick={this.closeModal}
            >
              &times;
            </button>
            <p className="egg-modal-eyebrow">Caught you</p>
            <h2 id="reactanceTitle">Well, well, well.</h2>
            <div className="egg-attrib-row">
              <div className="egg-attrib-photo">
                <img
                  src={require("../../../../../assets/images/landings/jack_brehm.png")}
                  alt="Portrait of Jack Brehm"
                  loading="lazy"
                />
              </div>
              <div className="egg-attrib-text">
                <p className="egg-attrib-quote">
                  When humans are told not to do something, the urge to do it
                  goes <em>up</em>, not down.
                </p>
                <p className="egg-attrib-caption">
                  <strong>Jack Brehm</strong> - psychologist - 1966
                </p>
              </div>
            </div>
            <p>You were told not to click. You clicked anyway.</p>
            <p>
              Welcome to <strong>reactance theory</strong>. It's why teenagers
              exist. It's why "wet paint" signs are essentially invitations.
              It's why every banned book in history has sold magnificently.
            </p>
            <p>
              You've just demonstrated lesson one of behavioural science in
              roughly 0.4 seconds. The other lessons are even more interesting.
            </p>
            <div className="egg-modal-actions">
              <a href="/courses/behavioural-science" className="btn btn-blue">
                See the course &rarr;
              </a>
              <button
                type="button"
                className="btn btn-outline-blue"
                onClick={this.closeModal}
              >
                I'll behave
              </button>
            </div>
          </>,
          "reactanceTitle",
        )}

        {this.renderModal(
          "fortyTwo",
          <>
            <button
              type="button"
              className="egg-modal-x"
              aria-label="Close"
              onClick={this.closeModal}
            >
              &times;
            </button>
            <p className="egg-modal-eyebrow">You found it</p>
            <h2 id="fortyTwoTitle">
              <span className="big-42">42</span>
            </h2>
            <p>The answer to life, the universe and everything.</p>
            <p>
              Frustratingly, no one in the book ever knew the question. We have
              spent over a decade writing 40+ courses trying to work it out.
            </p>
            <p className="muted-line">You're welcome.</p>
            <div className="egg-modal-actions">
              <a href="/courses" className="btn btn-blue">
                Explore the courses
              </a>
              <button
                type="button"
                className="btn btn-outline-blue"
                onClick={this.closeModal}
              >
                Close
              </button>
            </div>
          </>,
          "fortyTwoTitle",
        )}

        <canvas
          ref={this.canvasRef}
          className={`egg-confetti ${this.state.firing ? "firing" : ""}`}
          aria-hidden="true"
        />
      </>
    );
  }
}

export default EasterEggs;
